/**
 * iter321 — One guest, one line. Tap it to see every session behind the
 * amount, take the cash in a single entry, write it off, or nudge them.
 */
import { useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import Avatar from './ui/avatar-initials';
import { ChevronDown, Loader2, Coins, Ban, MessageCircle, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const STATUS = {
  pending: { label: 'unpaid', cls: 'text-rose-600' },
  collected: { label: 'paid', cls: 'text-emerald-600' },
  waived: { label: 'written off', cls: 'text-muted-foreground' },
  not_charged: { label: 'fee not charged yet', cls: 'text-amber-600' },
};

export const GuestRow = ({ g, groupId, isAdmin, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cash, setCash] = useState('');

  const act = async (fn, okMsg) => {
    setBusy(true);
    try {
      const r = await fn();
      toast.success(r?.data?.message || okMsg);
      setCash('');
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not do that');
    } finally {
      setBusy(false);
    }
  };

  const collect = () => act(
    () => api.post(`/groups/${groupId}/guests/${g.key}/collect`,
      cash === '' ? {} : { cash_amount: Number(cash) }),
    'Cash collected',
  );
  const waive = () => {
    if (!window.confirm(`Write off everything ${g.name} owes? The group fund won't get this money.`)) return;
    act(() => api.post(`/groups/${groupId}/guests/${g.key}/waive`), 'Written off');
  };
  const remind = () => act(() => api.post(`/groups/${groupId}/guests/${g.key}/remind`), 'Reminder sent');
  const chargeSession = (sessionId) => act(
    () => api.post(`/sessions/${sessionId}/charge-fee`), 'Session fees charged',
  );

  return (
    <Card className="p-3 rounded-2xl border-2 border-dashed" data-testid={`guest-item-${g.key}`}>
      <button type="button" className="w-full flex items-center gap-3 text-left"
        onClick={() => setOpen((o) => !o)} data-testid={`guest-expand-${g.key}`}>
        <Avatar name={g.name} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="font-semibold tracking-tight truncate">{g.name}</p>
            <span className="text-xs uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-bold">
              Guest
            </span>
          </div>
          <p className="text-xs text-muted-foreground tabular-nums">
            {g.phone || 'no number'} · {g.sessions} session{g.sessions > 1 ? 's' : ''}
            {g.last_session_date ? ` · last ${g.last_session_date}` : ''}
          </p>
          {g.added_by_name && (
            <p className="text-xs text-muted-foreground truncate" data-testid={`guest-added-by-${g.key}`}>
              added by {g.added_by_name}{g.last_session_title ? ` · ${g.last_session_title}` : ''}
            </p>
          )}
        </div>
        {g.pending_amount > 0 ? (
          <span className="shrink-0 text-xs font-bold px-2 py-1 rounded-full bg-rose-500/10 text-rose-600 tabular-nums"
            data-testid={`guest-pending-${g.key}`}>
            ₹{Math.round(g.pending_amount).toLocaleString('en-IN')} due
          </span>
        ) : (
          <span className="shrink-0 text-xs font-bold px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600"
            data-testid={`guest-settled-${g.key}`}>
            Settled
          </span>
        )}
        <ChevronDown className={`w-4 h-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="mt-3 pt-3 border-t space-y-2" data-testid={`guest-details-${g.key}`}>
          {(g.dues || []).map((d, i) => {
            const st = STATUS[d.status] || STATUS.pending;
            return (
              <div key={`${d.session_id}-${i}`} className="flex items-center gap-2"
                data-testid={`guest-due-${g.key}-${i}`}>
                <Receipt className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold truncate">{d.session_title || 'Session'}</p>
                  <p className={`text-xs ${st.cls}`}>{d.session_date} · {st.label}</p>
                </div>
                <span className="text-xs font-bold tabular-nums shrink-0">₹{Math.round(d.amount)}</span>
                {!d.charged && isAdmin && (
                  <Button size="sm" variant="ghost" className="h-7 rounded-full text-xs shrink-0"
                    disabled={busy} onClick={() => chargeSession(d.session_id)}
                    data-testid={`guest-charge-session-${g.key}-${i}`}>
                    Charge
                  </Button>
                )}
              </div>
            );
          })}

          {isAdmin && g.pending_amount > 0 && (
            <div className="pt-2 space-y-2">
              <div className="flex gap-2">
                <Input type="number" inputMode="numeric" placeholder={`₹${Math.round(g.pending_amount)} — cash received`}
                  value={cash} onChange={(e) => setCash(e.target.value)}
                  className="h-9 rounded-xl border-2 text-xs" data-testid={`guest-cash-input-${g.key}`} />
                <Button size="sm" className="rounded-full h-9 text-xs shrink-0" disabled={busy}
                  onClick={collect} data-testid={`guest-collect-${g.key}`}>
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Coins className="w-3.5 h-3.5 mr-1" /> Collect cash</>}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Leave blank to take the full amount. A smaller amount settles the oldest sessions and writes off the difference.
              </p>
              <div className="flex gap-2">
                {g.phone && (
                  <Button size="sm" variant="outline" className="rounded-full h-8 text-xs flex-1" disabled={busy}
                    onClick={remind} data-testid={`guest-remind-${g.key}`}>
                    <MessageCircle className="w-3.5 h-3.5 mr-1" /> Remind
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="rounded-full h-8 text-xs flex-1 text-muted-foreground"
                  disabled={busy} onClick={waive} data-testid={`guest-waive-${g.key}`}>
                  <Ban className="w-3.5 h-3.5 mr-1" /> Write off
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};
